import { IDomainsSlice } from "@/redux/slices/domains/types.ts";
import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks.ts";
import * as S from "./styles.ts";
import {
  Button,
  P,
  Select,
  Skeleton,
  UniversalLink,
} from "@template/components";
import { Check, Star } from "@template/icons";
import { useState } from "react";
import { postCartItem } from "@/redux/slices/user/thunks/postCartItem.ts";
import { deleteCartItem } from "@/redux/slices/user/thunks/deleteCartItem.ts";
import DomainRegistrationTerm from "@template/blocks/domain/DomainNameCustomSearchResults/DomainRegistrationTerm";

const Result = ({
  domain,
  primary,
  domainsLoading,
}: {
  domain?: IDomainsSlice["search"]["results"]["primary"];
  primary?: boolean;
  domainsLoading?: boolean;
}) => {
  const dispatch = useTypedDispatch();

  const currency = useTypedSelector(
    (state) => state.store.site_version.currency
  );
  const cartItems = useTypedSelector((state) => state.user.cart.items);

  const [selectedDuration, setSelectedDuration] = useState(1);

  if (!domainsLoading && !domain) return;

  const inCart = cartItems.find((item) => item.name === domain.domain_name);

  const domainOptions = Object.keys(domain?.prices || {})?.map((priceCat) => {
    const price = domain.prices[priceCat];
    return {
      label: `${priceCat} Year${Number(priceCat) > 1 ? "s" : ""} - ${currency === "USD" ? "$" : "£"}${price / 100}${domain.is_on_promotion ? " (Promo)" : ""}`,
      key: Number(priceCat),
    };
  });

  const onRemoveFromCart = () => {
    if (!inCart) return;
    dispatch(deleteCartItem(inCart.id));
  };

  const onAddToBasket = () => {
    dispatch(
      postCartItem({
        itemType: 54,
        itemSubType: 1,
        itemData: {
          domain_name: domain.domain_name,
          tld: domain.tld,
          period: selectedDuration.toString(),
          action: 1,
          premium: domain.premium_price ? true : undefined,
        },
      })
    );
  };

  const onBackorder = () => {
    dispatch(
      postCartItem({
        itemType: 54,
        itemSubType: 1,
        itemData: {
          domain_name: domain.domain_name,
          tld: domain.tld,
          period: "1",
          action: 18,
        },
      })
    );
  };

  const onTransfer = () => {
    dispatch(
      postCartItem({
        itemType: 54,
        itemSubType: 1,
        itemData: {
          domain_name: domain.domain_name,
          tld: domain.tld,
          period: "1",
          action: 16,
          premium: domain.premium_price ? true : undefined,
        },
      })
    );
  };

  return (
    <>
      <Skeleton
        hideSkeleton={!domainsLoading}
        style={{ width: "100%" }}
      >
        <S.Result $primary={primary}>
          {domain && (
            <>
              <S.Left>
                <S.Domain>
                  <span>
                    {domain.domain_name.split(".")[0]}
                    <strong>.{domain.tld}</strong>
                  </span>
                  {primary && (
                    <>
                      {domain.available
                        ? " is available!"
                        : " is already taken!"}
                    </>
                  )}
                </S.Domain>
                {primary && (
                  <P>
                    {domain.available ? (
                      "Buy it now before it's taken!"
                    ) : (
                      <UniversalLink
                        target="_blank"
                        href={`https://who.is/whois/${domain.domain_name}`}
                      >
                        Who owns it?
                      </UniversalLink>
                    )}
                  </P>
                )}
              </S.Left>
              {domain.is_premium && (
                <S.Premium>
                  <Star
                    width={16}
                    height={16}
                  />
                  PREMIUM
                </S.Premium>
              )}
              <S.Right>
                {inCart ? (
                  <Button
                    color="success"
                    onClick={onRemoveFromCart}
                    disabled={inCart?.loading}
                  >
                    <Check /> In Cart
                  </Button>
                ) : domain.available ? (
                  <>
                    {primary ? (
                      <Select
                        variant={primary ? "solid" : "light"}
                        selected={selectedDuration}
                        onChange={setSelectedDuration}
                        options={domainOptions}
                        disabled={inCart?.loading}
                      />
                    ) : (
                      <DomainRegistrationTerm
                        promo={domain.is_on_promotion}
                        options={domain.prices}
                        onSelect={setSelectedDuration}
                        selected={selectedDuration}
                        loading={inCart?.loading}
                      />
                    )}
                    <Button
                      size={primary ? "md" : "sm"}
                      title={`Click to add ${domain.domain_name} to your basket`}
                      variant={primary ? "solid" : "outline"}
                      onClick={onAddToBasket}
                      disabled={inCart?.loading}
                    >
                      Add to Basket
                    </Button>
                  </>
                ) : (
                  <>
                    {domain.can_backorder && (
                      <Button
                        size={primary ? "md" : "sm"}
                        title={`Click to backorder ${domain.domain_name}`}
                        variant={primary ? "solid" : "outline"}
                        onClick={onBackorder}
                        disabled={inCart?.loading}
                      >
                        Backorder
                      </Button>
                    )}
                    {domain.can_transfer && (
                      <Button
                        size={primary ? "md" : "sm"}
                        variant="outline"
                        title={`Click to transfer ${domain.domain_name}`}
                        onClick={onTransfer}
                        disabled={inCart?.loading}
                      >
                        Transfer
                      </Button>
                    )}
                  </>
                )}
              </S.Right>
            </>
          )}
        </S.Result>
      </Skeleton>
    </>
  );
};
export default Result;
